import React from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { ExperimentAnalytics } from '../../../api/pricingVariants';

interface FunnelTableProps {
  isDark: boolean;
  analytics: ExperimentAnalytics | null;
  isLoading: boolean;
}

const formatMoney = (value: number) =>
  new Intl.NumberFormat('uz-UZ', {
    style: 'currency',
    currency: 'UZS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/**
 * Воронка по вариантам: назначено → заказ → оплата → выручка → ARPU.
 *
 * ARPU, а не конверсия, — решающая метрика (ADR 0014): при кратном разбросе
 * дешёвый вариант почти гарантированно выигрывает по конверсии и может при
 * этом проиграть по деньгам.
 */
export function FunnelTable({ isDark, analytics, isLoading }: FunnelTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  if (!analytics || analytics.variants.length === 0) {
    return (
      <div className={cn('py-12 text-center text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
        Вариантов пока нет. Создайте контрольный вариант и один-два тестовых —
        замер начнётся с момента запуска набора.
      </div>
    );
  }

  const best = analytics.variants
    .filter((v) => v.is_readable)
    .sort((a, b) => b.arpu - a.arpu)[0];

  const headCell = cn('font-semibold whitespace-nowrap', isDark ? 'text-slate-400' : 'text-slate-500');
  const cell = cn('whitespace-nowrap', isDark ? 'text-slate-300' : 'text-slate-700');

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}>
            <TableHead className={headCell}>Вариант</TableHead>
            <TableHead className={headCell}>Доля набора</TableHead>
            <TableHead className={headCell}>Назначено</TableHead>
            <TableHead className={headCell}>Создали заказ</TableHead>
            <TableHead className={headCell}>Оплатили</TableHead>
            <TableHead className={headCell}>Заказ → оплата</TableHead>
            <TableHead className={headCell}>Конверсия</TableHead>
            <TableHead className={headCell}>Выручка</TableHead>
            <TableHead className={headCell}>ARPU</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {analytics.variants.map((row) => (
            <TableRow
              key={row.id}
              className={cn(
                'transition-colors',
                isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50',
                best?.id === row.id && (isDark ? 'bg-emerald-500/5' : 'bg-emerald-50/60'),
              )}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <span className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}>
                    {row.name}
                  </span>
                  {row.is_control && (
                    <Badge className="rounded-lg border-0 bg-slate-500/10 text-slate-500">
                      контроль
                    </Badge>
                  )}
                  {row.status === 'archived' && (
                    <Badge className="rounded-lg border-0 bg-red-500/10 text-red-500">
                      архив
                    </Badge>
                  )}
                </div>
                {row.transferred > 0 && (
                  <p className={cn('text-xs mt-1', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    переведено и исключено из сравнения: {row.transferred}
                  </p>
                )}
              </TableCell>
              <TableCell className={cell}>
                {row.status === 'active' ? `${row.share_percent}%` : '—'}
              </TableCell>
              <TableCell className={cn(cell, 'font-medium')}>{row.assigned}</TableCell>
              <TableCell className={cell}>
                {row.users_with_orders}
                <span className={cn('text-xs ml-1', isDark ? 'text-slate-500' : 'text-slate-400')}>
                  / {row.orders_count} шт
                </span>
              </TableCell>
              <TableCell className={cell}>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{row.paid_orders}</span>
                  {!row.is_readable && row.assigned > 0 && (
                    <span
                      title={`Меньше ${analytics.min_payments_for_decision} оплат — по этой группе решение не принимается`}
                      className="text-amber-500"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className={cell}>{row.order_to_payment_rate}%</TableCell>
              <TableCell className={cell}>{row.conversion}%</TableCell>
              <TableCell className={cell}>{formatMoney(row.revenue)}</TableCell>
              <TableCell
                className={cn(
                  'font-semibold whitespace-nowrap',
                  best?.id === row.id
                    ? 'text-emerald-500'
                    : isDark
                      ? 'text-white'
                      : 'text-[#111111]',
                )}
              >
                {formatMoney(row.arpu)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
